import React from "react";
import { Store, Award, Truck, Sparkles, TrendingUp, Shield } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionLabel from "@/components/SectionLabel";
import FormWithWhatsApp from "@/components/FormWithWhatsApp";
import { buildWaLink } from "@/lib/whatsapp";

const REASONS = [
  {
    id: "fast-movers",
    title: "Fast-moving product range",
    body: "Stock the products that farmers and vets already ask for — including the WORMIZAN and BIOTRIM ranges.",
    Icon: TrendingUp,
  },
  {
    id: "support",
    title: "Training & visibility support",
    body: "Agrovet product training, in-store demonstrations and field engagement.",
    Icon: Sparkles,
  },
  {
    id: "trust",
    title: "Built on Biodeal's 25 years",
    body: "Sell with the credibility of a Kenyan animal health manufacturer with deep field experience.",
    Icon: Award,
  },
  {
    id: "supply",
    title: "Reliable supply",
    body: "Practical pack sizes for the East African market, supported by the Biodeal field team.",
    Icon: Truck,
  },
];

const CATEGORIES = [
  "Dewormers (WORMIZAN, LEVAZAN, ABEZOLE)",
  "Antibiotics (BIOTRIM VET, BIOTRIM VET PLUS, DOXY-TYL VET POWDER)",
  "Supplements (Bio-Start, VitaVet WS, DCP, BIOMIX range)",
  "Biosecurity (OMNICIDE, Kleenol, Biodine, Hycolin)",
  "Udder & Dairy (Biosalve Medicated Milking Salve)",
  "Companion Animal (POPVET TOTAL, JointGuard)",
  "Veterinary Instruments",
  "Cold Chain & Vaccine Carriers (Haier range)",
];

export default function Stockist() {
  return (
    <div data-testid="stockist-page">
      <PageHero
        testId="stockist-hero"
        eyebrow="Become a Stockist"
        title="Sell with the credibility of Biodeal Animal Health."
        lede="Agrovets, vet clinics, distributors and farm stores — apply to stock the Biodeal Animal Health range and serve farmers better."
        image="https://images.pexels.com/photos/2933243/pexels-photo-2933243.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1600"
        primaryCta={{ label: "Apply Below", href: "#stockist-form" }}
        secondaryCta={{
          label: "WhatsApp the Stockist Team",
          href: buildWaLink("Hello Biodeal Animal Health, I run an agrovet and would like information on stocking your products."),
          external: true,
        }}
      />

      {/* Reasons */}
      <section className="py-20 md:py-28 bg-bah-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-12">
            <div className="lg:col-span-7">
              <SectionLabel>Why stock Biodeal Animal Health</SectionLabel>
              <h2 className="mt-4 font-display text-3xl md:text-5xl text-bah-ink leading-tight">
                A range your customers will return for.
              </h2>
            </div>
            <p className="lg:col-span-5 text-bah-mute md:text-lg">
              Best-selling formulas, practical pack sizes and a technical team that supports you on the ground.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {REASONS.map((r) => (
              <div
                key={r.id}
                className="card-rise bg-white border border-bah-line rounded-2xl p-6"
                data-testid={`stockist-reason-${r.id}`}
              >
                <div className="h-11 w-11 rounded-xl bg-bah-orange/10 text-bah-orange grid place-content-center">
                  <r.Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display text-xl text-bah-ink leading-snug">{r.title}</h3>
                <p className="mt-2 text-sm text-bah-mute leading-relaxed">{r.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product categories to stock */}
      <section className="py-20 md:py-28 bg-bah-ink text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow !text-bah-yellow">Available categories</div>
            <h2 className="mt-4 font-display text-3xl md:text-5xl leading-tight">
              The categories available for stockists.
            </h2>
            <p className="mt-5 text-white/75 md:text-lg max-w-md">
              From day-1 chick support to dewormers, antibiotics, biosecurity,
              udder care, companion animal, instruments and cold-chain — the
              full Biodeal Animal Health ecosystem.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {CATEGORIES.map((c) => (
                <li
                  key={c}
                  className="rounded-xl bg-white/[0.05] border border-white/10 px-4 py-3 text-sm font-semibold"
                >
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Form */}
      <section
        id="stockist-form"
        className="py-20 md:py-28 bg-bah-paperAlt scroll-mt-28"
        data-testid="stockist-form-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionLabel>Apply</SectionLabel>
            <h2 className="mt-4 font-display text-3xl md:text-4xl text-bah-ink leading-tight">
              Apply to become a Biodeal Animal Health stockist.
            </h2>
            <p className="mt-4 text-bah-mute">
              Share your business details — the BAH commercial team will be in touch.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 text-sm text-bah-green font-semibold">
              <Shield className="h-4 w-4" /> Your information is used only to evaluate this application.
            </div>
          </div>
          <div className="lg:col-span-7">
            <FormWithWhatsApp
              endpoint="/forms/stockist"
              testidPrefix="stockist"
              submitLabel="Apply to Become a Stockist"
              successText="We've received your application. The Biodeal Animal Health commercial team will be in touch."
              waMessageBuilder={(v) =>
                `Hello Biodeal Animal Health, stockist application — ${v.business_name || "[business]"} (${v.business_type || "[type]"}) in ${v.location || "[location]"}. Contact: ${v.contact_person || "[contact]"}. ${
                  v.products_interested ? `Products interested in: ${v.products_interested}. ` : ""
                }${v.message || ""}`
              }
              fields={[
                { name: "business_name", label: "Business name", required: true, placeholder: "e.g. ABC Agrovet Ltd" },
                { name: "contact_person", label: "Contact person", required: true, placeholder: "Owner or manager" },
                {
                  name: "business_type",
                  label: "Business type",
                  required: true,
                  options: ["Agrovet", "Vet clinic", "Distributor", "Farm store", "Other"],
                },
                { name: "location", label: "Location", required: true, placeholder: "Town / county" },
                { name: "phone", label: "Phone / WhatsApp", required: true, placeholder: "+254…" },
                { name: "email", label: "Email", type: "email" },
                {
                  name: "products_interested",
                  label: "Products interested in",
                  full: true,
                  placeholder: "Categories or specific products (e.g. WORMIZAN, BIOTRIM VET, BIOMIX, instruments…)",
                },
                {
                  name: "message",
                  label: "Message",
                  textarea: true,
                  rows: 4,
                  full: true,
                  placeholder: "Anything we should know — current suppliers, customer base, store size…",
                },
              ]}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
